import { skipToken } from "@reduxjs/toolkit/query";
import {
  flattenPlaybookTreeWithDepth,
  type PlaybookNode,
  type PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import type { RecordingAssessmentFeedbackStatus } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { createPath, Navigate, useSearchParams } from "react-router";
import { useRecordingAssessmentFeedbackForPlaybookQuery } from "../api/entities/playbook.js";
import { appPaths } from "../app/appPaths.js";
import { useResearchAgentProps } from "../features/researchAgent/researchAgentContext.js";
import { PlaybookEditorLoadingState } from "./playbookEditorLoadingState.js";
import { usePlaybookEditorOutletContext } from "./playbookEditorOutletContext.js";
import {
  buildManualFeedbackReviewSearchParams,
  buildPlaybookEditLocation,
  resolveFeedbackReviewUrlState,
} from "./playbookEditorUrlState.js";
import {
  buildPlaybookOpenFeedbackCounts,
  getInitialFeedbackReviewNodeId,
} from "./playbookFeedbackReview/feedbackReviewTreeHelpers.js";
import { buildFeedbackSuggestionUserPrompt } from "./playbookFeedbackReview/feedbackSuggestionPrompt.js";
import { PlaybookFeedbackReviewTab } from "./playbookFeedbackReview/playbookFeedbackReviewTab.js";

export const PlaybookFeedbackPage: FC = () => {
  const { playbookId, playbookIdParam } = usePlaybookEditorOutletContext();

  return playbookId ? (
    <ExistingPlaybookFeedbackPage />
  ) : (
    <Navigate
      to={appPaths.settingsPlaybookEdit.build({
        playbookId: playbookIdParam,
      })}
      replace
    />
  );
};

const ExistingPlaybookFeedbackPage: FC = () => {
  const context = usePlaybookEditorOutletContext();
  const researchAgent = useResearchAgentProps();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data,
    liveTree,
    playbookId,
    openFeedback,
    isOpenFeedbackLoading,
    isOpenFeedbackError,
    onStableSelectionChange,
    onReviewStatusChange,
  } = context;

  const reviewTree = data?.tree;
  const flattenedNodes = useMemo(
    () => (reviewTree ? flattenPlaybookTreeWithDepth(reviewTree) : []),
    [reviewTree],
  );
  const openFeedbackCounts = useMemo(
    () => buildPlaybookOpenFeedbackCounts(openFeedback),
    [openFeedback],
  );
  const fallbackNodeId = useMemo(
    () => getInitialFeedbackReviewNodeId(flattenedNodes, openFeedbackCounts),
    [flattenedNodes, openFeedbackCounts],
  );
  const urlState = useMemo(
    () =>
      resolveFeedbackReviewUrlState({
        currentSearchParams: searchParams,
        flattenedNodes,
        fallbackNodeId,
      }),
    [fallbackNodeId, flattenedNodes, searchParams],
  );
  const currentQuery = searchParams.toString();
  const canonicalQuery = urlState.searchParams.toString();

  // The rows for the selected status. For "open" this shares the editor
  // shell's cache entry; resolved and outdated fetch on first visit.
  // `currentData` (not `data`) so a status switch shows a loading state
  // rather than the previous status's rows under the new heading.
  const {
    currentData: feedback,
    isFetching: isFeedbackFetching,
    isError: isFeedbackError,
  } = useRecordingAssessmentFeedbackForPlaybookQuery(
    playbookId ? { playbookId, status: urlState.status } : skipToken,
  );

  useEffect(() => {
    if (
      reviewTree &&
      !isOpenFeedbackLoading &&
      !isOpenFeedbackError &&
      canonicalQuery !== currentQuery
    ) {
      setSearchParams(new URLSearchParams(canonicalQuery), { replace: true });
    }
  }, [
    canonicalQuery,
    currentQuery,
    isOpenFeedbackError,
    isOpenFeedbackLoading,
    reviewTree,
    setSearchParams,
  ]);

  useEffect(() => {
    if (isOpenFeedbackLoading || isOpenFeedbackError) {
      return;
    }
    onStableSelectionChange(urlState.selectedNodeId);
    onReviewStatusChange(urlState.status);
  }, [
    isOpenFeedbackError,
    isOpenFeedbackLoading,
    onReviewStatusChange,
    onStableSelectionChange,
    urlState.selectedNodeId,
    urlState.status,
  ]);

  const handlePickNode = useCallback(
    (nodeId: PlaybookNodeId) => {
      const nextSearchParams = buildManualFeedbackReviewSearchParams({
        currentSearchParams: searchParams,
        selectedNodeId: nodeId,
        status: urlState.status,
      });
      onStableSelectionChange(nodeId);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [onStableSelectionChange, searchParams, setSearchParams, urlState.status],
  );

  const handleStatusChange = useCallback(
    (status: RecordingAssessmentFeedbackStatus) => {
      const nextSearchParams = buildManualFeedbackReviewSearchParams({
        currentSearchParams: searchParams,
        selectedNodeId: urlState.selectedNodeId,
        status,
      });
      onReviewStatusChange(status);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [
      onReviewStatusChange,
      searchParams,
      setSearchParams,
      urlState.selectedNodeId,
    ],
  );

  // Suggest edits hands off to Ask Alpharun: open its panel and start a thread
  // with the request. The playbook is grounded by this route's page context.
  const handleSuggestEdits = useCallback(
    (node: PlaybookNode) => {
      researchAgent?.onOpenChat();
      researchAgent?.onNewThreadSelect({
        initialUserMessageText: buildFeedbackSuggestionUserPrompt(node),
      });
    },
    [researchAgent],
  );

  // Where the tab lands depends on the open feedback, so nothing sensible can
  // show until it arrives — rendering early would flash the first criterion
  // and then move to the first node with open feedback.
  return (
    <div className="min-h-0 flex-1 overflow-hidden">
      {reviewTree && liveTree && !isOpenFeedbackLoading ? (
        <PlaybookFeedbackReviewTab
          feedback={feedback ?? []}
          isFeedbackLoading={isFeedbackFetching && feedback === undefined}
          isFeedbackError={isFeedbackError}
          openFeedbackCounts={openFeedbackCounts}
          draftTree={reviewTree}
          liveTree={liveTree}
          pickedNodeId={urlState.selectedNodeId}
          onPickNode={handlePickNode}
          status={urlState.status}
          onStatusChange={handleStatusChange}
          editHref={buildPlaybookEditHref(
            context.playbookIdParam,
            urlState.selectedNodeId,
          )}
          onSuggestEdits={handleSuggestEdits}
        />
      ) : (
        <PlaybookEditorLoadingState />
      )}
    </div>
  );
};

function buildPlaybookEditHref(
  playbookIdParam: string,
  selectedNodeId: PlaybookNodeId | null,
): string {
  return createPath(
    buildPlaybookEditLocation({
      playbookIdParam,
      selectionId: selectedNodeId,
    }),
  );
}
